import { ItemClass, ItemSlot, StoredItem, eqItemClasses, eqItemSlots, makeItemElement, parseItemStat } from "LibMargoWorld/item";

import { addItemRarityClass } from "./item-loader";
import { ModalType, showModal } from "./modal";

const STORAGE_KEY = `margoworld-eq-set-v1`;

function isAllowedEqItem(it: StoredItem) {
    return eqItemClasses.includes(it.cl) || it.extendedID?.startsWith("blessing:");
}

export function initEqSet() {
    if (!checkSetExists()) {
        // Eq set building disabled on current page - check for summary
        if (document.querySelector(".eq-set-summary")) {
            document.querySelector(".eq-set-summary")!.innerHTML = buildEqSetSummary();
            const btt = document.querySelector("#set-save-btt") as HTMLButtonElement;
            btt.innerHTML = "Edytuj zestaw";
            btt.addEventListener("click", saveSetAsOwn);
        }
        return;
    }

    const items = readStoredItems().filter(isAllowedEqItem);
    items.forEach(insertEqItemElement);

    document.addEventListener("contextmenu", (e) => {
        if (e.target instanceof HTMLElement) {
            let it = <HTMLElement>e.target.closest(".margonem_item");
            if (!it && e.target.closest(".item") && e.target.closest(".item-display")) {
                it = <HTMLElement>e.target.closest(".item-display");
            }
            if (it && checkSetExists() && !e.ctrlKey) {
                onItemClick(it);
                e.preventDefault();
            }
        }
    });

    document.querySelector(".eq-set-controls .btn-primary")!.addEventListener("click", () => {
        const ids = readStoredItems().map((it) => it.extendedID ?? it.id);
        location.href = `/item/set/${ids.join(",")}`;
    });
    document.querySelector(".eq-set-controls .btn-danger")!.addEventListener("click", () => {
        showModal({
            type: ModalType.CUSTOM,
            content: "Czy na pewno chcesz wyczyścić aktualny zestaw ekwipunku?",
            options: [
                {
                    text: "Anuluj",
                    clb() {},
                },
                {
                    class: "danger",
                    text: "Potwierdź",
                    clb() {
                        for (const it of readStoredItems()) {
                            removeEqItem(it);
                        }
                        document.querySelectorAll(".eq-set-items .itemborder").forEach((el) => el.remove());
                    },
                },
            ],
        });
    });
}

export function getEqItemBySlot(slot: ItemSlot) {
    return readStoredItems().find((it) => eqItemSlots[it.cl] == slot);
}

const summableStats = [
    "ac",
    "acdmg",
    "resacdmg",
    "absorb",
    "absorbm",
    "abdest",
    "resfire",
    "resfrost",
    "reslight",
    "act",
    "resdmg",
    "crit",
    "lowcrit",
    "critval",
    "critmval",
    "lowcritallval",
    "ds",
    "dz",
    "di",
    "da",
    "energybon",
    "endest",
    "manabon",
    "manadest",
    "resmanaendest",
    "hp",
    "heal",
    "lowheal2turns",
    "adest",
    "evade",
    "lowevade",
    "blok",
    "sa",
    "slow",
    "fire",
    "frost",
    "light",
    "dmg",
    "contra",
    "pierce",
    "pierceb",
    "wound",
    "poison",
    "legbon",
    "manafatig",
    "enfatig",

    "socket_injection_legbon",
    "socket_fleeting_legbon",
];

const StatOrder = [
    "ac",
    "abdest",
    "acdmg",
    "act",
    "adest",
    "ammo",
    "binds",
    "blok",
    "cansplit",
    "capacity",
    "crit",
    "critmval",
    "critval",
    "da",
    "dmg",
    "di",
    "ds",
    "dz",
    "endest",
    "enfatig",
    "energybon",
    "evade",
    "fire",
    "light",
    "frost",
    "heal",
    "hp",
    "hpbon",
    "legbon",
    "lowcrit",
    "lowevade",
    "amount",
    "cansplit",
    "capacity",
    "cursed",
    "lvl",
    "lvlnext",
    "lvlupgcost",
    "lvlupgs",
    "manabon",
    "manadest",
    "manafatig",
    "opis",
    "pdmg",
    "permbound",
    "pierce",
    "poison",
    "pierceb",
    "lowheal2turns",
    "resacdmg",
    "lowcritallval",
    "resmanaendest",
    "rarity",
    "reqp",
    "resdmg",
    "resfire",
    "resfrost",
    "reslight",
    "sa",
    "slow",
    "wound",
    "absorb",
    "absorbm",
];

function getEqItems() {
    return Array.from(document.querySelectorAll(".eq-set-items .margonem_item")).map((el) => <StoredItem>JSON.parse(el.getAttribute("data-itemtip")!));
}

function saveSetAsOwn() {
    getEqItems().forEach(saveEqItem);
    location.href = "/item";
}

function buildEqSetSummary() {
    const items = getEqItems();

    const nSections = 16;

    const summedStats: Record<string, string> = {};
    const legbons: string[] = [];
    const wounds: string[] = [];
    const profs = ["w", "b", "h", "m", "p", "t"];
    for (const it of items) {
        const stat = parseItemStat(it.stat);
        for (const key in stat) {
            if (key == "reqp") {
                for (let i = 0; i < profs.length; ++i) {
                    const p = profs[i];
                    if (!stat[key].includes(p)) {
                        profs.splice(i, 1);
                        --i;
                    }
                }
            }

            if (!summableStats.includes(key)) continue;

            const val = stat[key];
            if (key == "legbon" || key == "socket_fleeting_legbon" || key == "socket_injection_legbon") {
                legbons.push(val);
            } else if (key == "wound") {
                wounds.push(val);
            } else if (key == "frost" || key == "poison") {
                if (summedStats[key]) {
                    const [slow, dmg] = val.split(",");
                    const [existingSlow, existingDmg] = summedStats[key].split(",");
                    const newDmg = parseInt(dmg) + parseInt(existingDmg);
                    const newSlow = Math.max(parseInt(slow), parseInt(existingSlow));
                    summedStats[key] = `${newSlow},${newDmg}`;
                } else {
                    summedStats[key] = val;
                }
            } else if (key == "light" || key == "dmg") {
                if (summedStats[key]) {
                    const [minDmg1, maxDmg1] = val.split(",");
                    const [minDmg2, maxDmg2] = summedStats[key].split(",");
                    summedStats[key] = `${parseInt(minDmg1) + parseInt(minDmg2)},${parseInt(maxDmg1) + parseInt(maxDmg2)}`;
                } else {
                    summedStats[key] = val;
                }
            } else if (key == "enfatig" || key == "manafatig") {
                const existing = summedStats[key];
                if (existing) {
                    const [chance1, val1] = existing.split(",");
                    const [chance2, val2] = val.split(",");
                    const totalChance = Math.min(100, Number(chance1) + Number(chance2));
                    const totalVal = val1 > val2 ? val1 : val2;
                    summedStats[key] = `${totalChance},${totalVal}`;
                } else {
                    summedStats[key] = val;
                }
            } else {
                summedStats[key] = String((summedStats[key] ? parseInt(summedStats[key]) : 0) + parseInt(val));
            }
        }
    }

    // @ts-ignore
    const h = createTipSectionsObject();
    const allSections: string[] = [];
    for (let i = 0; i < nSections; ++i) allSections[i] = "";

    const turboSwitchWrapper = (sections: string[], stats: string) => {
        // @ts-ignore
        const section = createTipSectionsObject();
        // @ts-ignore
        window.MargoTipsParser.turboSwich(section, h, { stat: stats });
        for (let i = 0; i < nSections; ++i) {
            const data = section.getSectionDataByIndex(i);
            if (data && Object.keys(data)) {
                for (const key in data) {
                    sections[i] += data[key];
                }
            }
        }
    };

    for (const key of StatOrder) {
        if (key == "wound") {
            for (const wnd of wounds) {
                const sections: string[] = [];
                for (let i = 0; i < nSections; ++i) sections[i] = "";

                turboSwitchWrapper(sections, `wound=${wnd}`);
                for (let i = 0; i < 10; ++i) {
                    allSections[i] += sections[i];
                }
            }
        }

        if (summedStats[key]) {
            const sections: string[] = [];
            for (let i = 0; i < nSections; ++i) sections[i] = "";

            turboSwitchWrapper(sections, `${key}=${summedStats[key]}`);

            if (key == "absorb" || key == "absorbm") {
                sections[1] = sections[3];
                sections[3] = "";
            }
            if (key == "dmg") {
                sections[2] = sections[1];
                sections[1] = "";
            }

            for (let i = 0; i < 10; ++i) {
                allSections[i] += sections[i];
            }
        }
    }

    for (const legbon of legbons) {
        // @ts-ignore
        turboSwitchWrapper(allSections, `legbon=${legbon}`);
    }

    if (profs.length) {
        turboSwitchWrapper(allSections, `reqp=${profs.join("")}`);
    } else {
        allSections[9] += `<span style="color:red">Zestaw zawiera konflikt profesji</span>`;
    }

    for (let i = 0; i < nSections; ++i) {
        if (allSections[i]) {
            allSections[i] = `<div class="item-tip-section s-${i}">${allSections[i]}</div>`;
        }
    }

    return `<div data-type="t_item" class="summary static custom tip-wrapper normal-tip">
        <div class="content">
            <b>Statystyki zestawu ekwipunku</b>
            ${allSections.join("")}
        </div>
    </div>`;
}

function onItemClick(it: HTMLElement) {
    const itemData = JSON.parse(it.getAttribute("data-itemtip") ?? it.getAttribute("data-item")!);
    if (verifyItemSchema(itemData)) {
        if (isAllowedEqItem(itemData)) {
            if (it.closest(".eq-set-items")) {
                it.closest(".itemborder")?.remove();
                removeEqItem(itemData);
            } else {
                saveEqItem(itemData);
                insertEqItemElement(itemData);
            }
        } else if (itemData.cl == ItemClass.BLESSING) {
            showModal({
                content:
                    'Z przyczyn technicznych, dodawanie błogosławieństw do ekwipunku jest możliwe tylko za pomocą <a href="/item/blessing">specjalnego edytora</a>.',
            });
        }
    }
}

function saveEqItem(it: StoredItem) {
    if (isAllowedEqItem(it)) {
        const items = readStoredItems();
        const slot = eqItemSlots[it.cl];
        const ind = items.findIndex((it) => eqItemSlots[it.cl] == slot);
        if (ind > -1) {
            items.splice(ind, 1);
        }
        items.push(it);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
}

function removeEqItem(it: StoredItem) {
    const items = readStoredItems();
    const ind = items.findIndex((eqIt) => eqIt.id == it.id);
    if (ind > -1) {
        items.splice(ind, 1);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }
}

function checkSetExists() {
    return !!document.querySelector(".eq-set-items.builder");
}

function insertEqItemElement(it: StoredItem) {
    const wrapper = document.querySelector(".eq-set-items");
    if (!wrapper) {
        console.error(`insertEqItemElement called but eq-set-items does not exist on current page`);
        return;
    }

    const item = makeItemElement(it);
    addItemRarityClass(item.querySelector(".margonem_item")!, it, parseItemStat(it.stat));
    const cl = `item-slot-${eqItemSlots[it.cl]}`;
    wrapper.querySelector(`.${cl}`)?.remove();
    item.classList.add(cl);
    wrapper.appendChild(item);
}

function verifyProps(data: any, props: string[], type: string) {
    return !Object.keys(data)
        .filter((p) => props.includes(p))
        .some((p) => typeof data[p] != type);
}

function verifyArray(arr: any, predicate: (val: any) => boolean) {
    return Array.isArray(arr) && !arr.some((val) => !predicate(val));
}

export function verifyItemSchema(data: any): data is StoredItem {
    return (
        verifyProps(data, ["id", "cl", "st", "version"], "number") &&
        verifyProps(data, ["icon", "stat"], "string") &&
        (data.pr == "" || typeof data.pr == "number")
    );
}

function readStoredItems() {
    const itemData = localStorage.getItem(STORAGE_KEY);
    if (!itemData) return [];

    try {
        const items = JSON.parse(itemData);
        if (verifyArray(items, verifyItemSchema) && items.length <= 9) {
            return <StoredItem[]>items;
        } else {
            console.log(itemData, items);
            console.error(`Invalid item schema`);
            return [];
        }
    } catch (e) {
        console.error(`Stored item data is corrupted`, e);
        return [];
    }
}
